import React, { useState, useEffect } from "react";
import Switch from "@mui/material/Switch";
import { toast } from "react-hot-toast";
import { useApiMutation } from "../../../hooks/useMutation";
import { useApiQuery } from "../../../hooks/useApiQuery";
import { ApiRequestOptions } from "../../../utils/api";
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SmsIcon from '@mui/icons-material/Sms';
import MessageIcon from '@mui/icons-material/Message';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Info } from "@mui/icons-material";
import Button from "../../../utils/Button";

interface FeatureSwitchesResponse {
  data: {
    messaging: {
      enabled: boolean;
    };
  };
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isEnabling: boolean;
}

function ConfirmationModal({ isOpen, onClose, onConfirm, isEnabling }: ConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999999] flex items-center justify-center"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onMouseDown={onClose}
      />

      <div
        className="relative z-10 w-[600px] max-w-[95%] bg-gradient-to-br from-gray-900 via-slate-900 to-black rounded-xl shadow-2xl p-6 text-white border border-gray-800"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="my-3 rounded-md bg-gray-800/70 backdrop-blur shadow-lg p-2 shadow-black w-fit">
            {isEnabling ? (
              <CheckCircleIcon fontSize="large" className="text-green-500" />
            ) : (
              <WarningIcon fontSize="large" className="text-red-500" />
            )}
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-xl">✕</button>
        </div>

        <h2 className="text-2xl font-semibold mb-3">
          {isEnabling ? "Enable SMS & Messaging Features" : "Disable SMS & Messaging Features"}
        </h2>

        <div className="mb-6">
          {isEnabling ? (
            <div>
              <p className="text-gray-300 mb-4">
                You are about to <span className="text-green-400 font-semibold">enable</span> SMS, messaging, and reminder features for all partners and canvassers.
              </p>
              <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-4 mb-4">
                <h3 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
                  <CheckCircleIcon fontSize="small" />
                  Features Being Enabled:
                </h3>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• SMS messaging capabilities for partners</li>
                  <li>• In-app messaging system for canvassers</li>
                  <li>• Election reminder notifications</li>
                  <li>• Automated SMS reminders to voters</li>
                  <li>• Text message verification systems</li>
                  <li>• Bulk SMS campaign tools</li>
                </ul>
              </div>
              <p className="text-xs text-gray-400">
                Partners will immediately regain access to all SMS and messaging modules. Canvassers will be able to send messages and voters will receive SMS reminders.
              </p>
            </div>
          ) : (
            <div>
              <p className="text-gray-300 mb-4">
                You are about to activate a <span className="text-red-400 font-semibold">GLOBAL KILL SWITCH</span> that will automatically and globally disable SMS and messaging features for all partners and canvassers.
              </p>
              <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4 mb-4">
                <h3 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                  <WarningIcon fontSize="small" />
                  Warning: Critical System Impact
                </h3>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• All SMS messaging will stop immediately</li>
                  <li>• In-app messaging will be disabled</li>
                  <li>• Election reminders will cease</li>
                  <li>• Voter outreach tools will fail</li>
                  <li>• Canvas communication will be limited</li>
                </ul>
              </div>
              <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-4">
                <h3 className="text-yellow-400 font-semibold mb-2 text-sm">⚠️ Disclaimer</h3>
                <p className="text-xs text-gray-400">
                  This action is irreversible without manual intervention. All partner organizations and their canvassers will lose access to communication tools until this feature is re-enabled. This may impact ongoing voter outreach campaigns and time-sensitive election activities.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            additionalClassname="flex-1 px-4 py-2 text-gray-900 transition-colors"
            label="Cancel"
            variant="tertiary"
          />
          <Button
            onClick={onConfirm}
            additionalClassname={`flex-1 px-4 py-2  transition-colors font-semibold ${isEnabling
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"
              }`}
              label={isEnabling ? "Enable Features" : "Disable Features"}
              variant="primary"
          >
            
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Controls() {
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<boolean | null>(null);

  // Fetch current feature switches state
  const { data: featureSwitchesData, isLoading: isLoadingFeatureSwitches, refetch: refetchFeatureSwitches } = useApiQuery<FeatureSwitchesResponse>({
    queryKey: ['feature-switches'],
    request: {
      method: 'GET',
      url: '/admin/feature-switches',
    },
  });

  // Get current messaging state from API response
  const smsMessagingEnabled = featureSwitchesData?.data?.messaging?.enabled ?? true;

  const toggleMutation = useApiMutation({
    onMutate: () => {
      toast.loading('Updating global SMS settings...', { id: 'sms-toggle-toast' });
    },
    onSuccess: () => {
      refetchFeatureSwitches();
      toast.success(
        pendingAction
          ? 'SMS and messaging features have been globally enabled for all partners.'
          : 'SMS and messaging features have been globally disabled for all partners.',
        { id: 'sms-toggle-toast' }
      );
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Failed to update SMS settings. Please try again.',
        { id: 'sms-toggle-toast' }
      );
    },
  });

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setPendingAction(newValue);
    setShowModal(true);
  };

  const handleConfirmToggle = async () => {
    if (pendingAction === null) return;

    setShowModal(false);

    try {
      const requestOptions: ApiRequestOptions = {
        method: 'PUT',
        url: `/admin/feature-switches/messaging`,
        data: {
          enabled: pendingAction,
        },
      };

      await toggleMutation.mutateAsync(requestOptions);
    } catch (err) {
      console.error("Failed to toggle SMS settings", err);
      // Error is handled by the mutation's onError callback
    }

    setPendingAction(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setPendingAction(null);
  };

  return (
    <div className="w-full">
      <div>
        <h1 className="text-xl font-bold mb-4 m-0">
          Control Panel
        </h1>
        <small className="block text-gray-600 mb-6">
          This control settings is built to handle Global kill switches and other critical system functions for partners, canvassers and admins inclusive.
        </small>

        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold text-gray-800">
                  Global SMS & Messaging Control
                </h2>
                <SmsIcon className="text-blue-600" />
                {/* <MessageIcon className="text-green-600" /> */}
                <NotificationsIcon className="text-orange-600" />
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Global control switch for all SMS, messaging, and reminder features across the platform.
              </p>
              <div className="text-xs text-gray-500">
                Current Status:{" "}
                <span className={`font-semibold ${smsMessagingEnabled ? "text-green-600" : "text-red-600"}`}>
                  {smsMessagingEnabled ? "ENABLED" : "DISABLED"}
                </span>
                {" "}| Affects: All partners, canvassers, and voter outreach systems
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-700">
                {smsMessagingEnabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={smsMessagingEnabled}
                onChange={handleToggleChange}
                disabled={toggleMutation.isPending || isLoadingFeatureSwitches}
                color="primary"
                size="medium"
              />
            </div>
          </div>
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-2"><Info /> Important Information</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• This control affects all SMS messaging, in-app communications, and automated reminders</li>
              <li>• When disabled, partners cannot send SMS messages or use communication tools</li>
              <li>• Canvassers will lose access to messaging features for field operations</li>
              <li>• Election reminders and voter outreach campaigns will be paused</li>
              <li>• Changes take effect immediately across all active sessions</li>
            </ul>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmToggle}
        isEnabling={pendingAction === true}
      />
    </div>
  );
}